
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle, Monitor, Video, Layers, Settings, Zap, Network, Cpu, Cable } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const FeaturesAndBenefitsDIDO = () => {
  const { t } = useLanguage();

  const keyFeatures = [
    {
      icon: <Video className="h-8 w-8 text-[#069E50]" />,
      title: "Advanced Video Processing",
      description: "4x4 scaler with quad processor delivering professional-grade video effects and processing capabilities"
    },
    {
      icon: <Monitor className="h-8 w-8 text-[#069E50]" />,
      title: "Video Wall & Edge Blending",
      description: "1-degree image rotation for portrait mode, contrast edge blending for side-by-side projectors"
    },
    {
      icon: <Layers className="h-8 w-8 text-[#069E50]" />,
      title: "Windowing & Multi-viewer",
      description: "Seamlessly switch and window up to 4 sources any size anywhere on the display"
    },
    {
      icon: <Settings className="h-8 w-8 text-[#069E50]" />,
      title: "Chroma Keying & OSD",
      description: "Overlay sources with chroma keying and bitmap/character-based OSD for inputs and outputs"
    },
    {
      icon: <Zap className="h-8 w-8 text-[#069E50]" />,
      title: "Pattern Generator",
      description: "Built-in pattern generation for signal integrity diagnosis and testing"
    },
    {
      icon: <Cpu className="h-8 w-8 text-[#069E50]" />,
      title: "USB 3.1 Capture Output",
      description: "Direct PC interface for Zoom Rooms, Teams, WebEx, and other collaboration platforms"
    }
  ];

  const videoCapabilities = [
    {
      title: "Video Wall Processing",
      items: [
        "1-degree image rotation for portrait and jigsaw puzzle mode videowalls",
        "Support for 1-2 outputs with precision rotation control",
        "90-degree increment rotation for 2 outputs",
        "Professional videowall configuration and management"
      ]
    },
    {
      title: "Advanced Effects Processing",
      items: [
        "Contrast edge blending for seamless side-by-side projector setups",
        "Chroma keying for professional source overlaying",
        "Bitmap and character-based OSD for all channels",
        "Real-time video effects processing"
      ]
    }
  ];

  const connectivityFeatures = [
    {
      title: "Input/Output Connectivity",
      items: [
        "Four HDMI 2.0 inputs with pre-installed input card",
        "Four HDMI 2.0 outputs with independent scaling",
        "Support for up to 4K2K 4:4:4 @60Hz video",
        "Future input card options for expanding connectivity"
      ]
    },
    {
      title: "Network & Control",
      items: [
        "10/100/1000Mbps LAN with PoE+ (PD) support",
        "Integrated web server for custom control pages",
        "RS-232 serial port and IR (bidirectional)",
        "Front keypad with OLED display for local control"
      ]
    },
    {
      title: "Audio Integration",
      items: [
        "Stereo line in/out audio processing",
        "Up to 32 channels audio support with break-away capability",
        "Optional Dante®/AES67 IP 2Ch audio integration",
        "Professional audio routing and processing"
      ]
    }
  ];

  const benefits = [
    "Multiple devices in one: Video Effects Processor, Windowing System, Video Wall Processor, Pattern Generator",
    "Advanced video processing with contrast edge blending, chroma keying, and image rotation",
    "Seamless windowing up to 4 sources locally to any of the 4 outputs",
    "1-degree image rotation capability for precise videowall configurations",
    "Optional Dante/AES67 integration for professional audio networking",
    "USB 3.1 capture output for direct computer integration",
    "Built-in pattern generator for system testing and diagnostics",
    "Web-based control interface with custom page support",
    "Auto sense switching for intelligent source detection",
    "Flexible mounting options: rack mount and under table/wall mount"
  ];

  const technicalSpecs = [
    {
      title: "Video Specifications",
      items: [
        "Video bandwidth: 600MHz for high-quality signal processing",
        "Support for up to 4K2K 4:4:4 @60Hz video resolution",
        "Quad scaler processors for independent output scaling",
        "Seamless switching with professional-grade performance"
      ]
    },
    {
      title: "Physical & Power",
      items: [
        "Compact design: 10.5\" x 8\" x 1.22\" black aluminum enclosure",
        "Lightweight: 2lb 4.6oz (1.04kg) for easy installation",
        "25 watts maximum power consumption with PoE+ support",
        "Operating temperature: 0~40ºC [32~100ºF]"
      ]
    }
  ];

  const handleViewProduct = () => {
    window.scrollTo(0, 0);
  };

  return (
    <div className="container mx-auto px-4 py-16">
      <div className="mb-8">
        <Link
          to="/product/dido-x4"
          className="mb-8 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to DIDO X4 Product Page
        </Link>
        <h1 className="mb-4 text-4xl font-bold tracking-tight">
          DIDO X4 - Features & Benefits
        </h1>
        <p className="text-lg text-muted-foreground max-w-3xl">
          Discover the powerful features and compelling benefits of the DIDO X4, one of the most advanced 4x4 scalers 
          on the market, delivering ultimate value through multiple devices in one professional solution.
        </p>
      </div>

      {/* Overview Section */}
      <div className="mb-16">
        <Card>
          <CardHeader>
            <CardTitle>Overview</CardTitle>
            <CardDescription>
              The ultimate 4x4 video processor combining multiple professional devices into one solution
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-lg leading-relaxed mb-4">
              The DIDO X4 is one of the most advanced 4x4 scaler on the market. The input card slot has a pre-installed 4 input HDMI card with future cards to come addressing various needs. These inputs can be routed to any of the four HDMI outputs with a quad scaler processor and advanced features.
            </p>
            <p className="text-lg leading-relaxed mb-4">
              These features consist of contrast edge blending for side-by-side projectors (2 Outputs), 1 degree image rotation for portrait and jigsaw puzzle mode videowalls (1-2 Outputs), chroma keying for overlaying of sources on top of others, and pattern generation for signal integrity diagnosing. In addition, the DIDO X4 can seamlessly switch and window 4 sources any size anywhere on the display.
            </p>
            <p className="text-lg leading-relaxed mb-4">
              Another amazing feature is the built-in USB 3.1 capture output for a PC making interfacing to Zoom Rooms, Teams, etc. even easier. Control can be sent to the 1G Ethernet port which also has built-in webpage setup support.
            </p>
            <p className="text-lg leading-relaxed font-semibold text-[#069E50]">
              Choice is important. That's why the DIDO X4 is the ultimate 4x4 video processor product on the market today.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Key Features Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8">Key Features</h2>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {keyFeatures.map((feature, index) => (
            <div key={index} className="bg-card rounded-lg p-6 border shadow-sm">
              <div className="mb-4">{feature.icon}</div>
              <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
              <p className="text-muted-foreground">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Video Capabilities Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8">Advanced Video Capabilities</h2>
        <div className="grid gap-6 md:grid-cols-2">
          {videoCapabilities.map((capability, index) => (
            <div key={index} className="bg-card rounded-lg p-6 border shadow-sm">
              <h3 className="text-lg font-semibold mb-3 text-[#069E50]">{capability.title}</h3>
              <ul className="space-y-2">
                {capability.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#069E50] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Connectivity Features Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8">Connectivity & Control Features</h2>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {connectivityFeatures.map((feature, index) => (
            <div key={index} className="bg-card rounded-lg p-6 border shadow-sm">
              <h3 className="text-lg font-semibold mb-3 text-[#069E50]">{feature.title}</h3>
              <ul className="space-y-2">
                {feature.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#069E50] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Technical Specifications Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8">Technical Specifications</h2>
        <div className="grid gap-6 md:grid-cols-2">
          {technicalSpecs.map((spec, index) => (
            <div key={index} className="bg-card rounded-lg p-6 border shadow-sm">
              <h3 className="text-lg font-semibold mb-3 text-[#069E50]">{spec.title}</h3>
              <ul className="space-y-2">
                {spec.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#069E50] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Business Benefits Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8">Key Benefits</h2>
        <div className="bg-card rounded-lg p-8 border shadow-sm">
          <h3 className="text-lg font-semibold mb-4 text-[#069E50]">
            The DIDO X4 is many devices in one product providing not just the ultimate experience but the ultimate value
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-[#069E50] mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="text-center bg-muted rounded-lg p-8">
        <h2 className="text-2xl font-bold mb-4">Ready to Learn More?</h2>
        <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
          Discover detailed specifications, documentation, and technical details for the DIDO X4 advanced 4x4 scaler.
        </p>
        <Link to="/product/dido-x4" onClick={handleViewProduct}>
          <Button size="lg">
            View DIDO X4 Product Details
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default FeaturesAndBenefitsDIDO;
